import React from 'react'
// libs
import moment from 'moment'
import styled from 'styled-components'
// components
import Grid from '@mui/material/Grid2'
import CheckCircle from '@mui/icons-material/CheckCircle'
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline'
// context
import { ObjectiveContext } from 'context/index'
// constants
import { OBJECT_ENTITY_NAMES } from 'constants/objects'
// views
import {
  Button,
  DueDateWrapper,
  DueIndicator,
  ProgressIndicator,
  TextSeparator,
  Wrapper
} from '../../../../../components/CompleteReview/views'
// themes
import colors from 'themes/colors'
// types
import { IField, IPlanAction, IPlanObjective, SystemProcessObject } from 'types/graphql'
// hooks
import { useStrategyCompletionProcess } from 'hooks/useStrategyCompletionProcess'
// utils
import get from 'lodash/get'
import { handleError } from 'utils/errors/handlers'

// styled
const InnerWrapper = styled(Grid)`
  margin-left: 5px;
  align-items: center;
`

const ButtonLabel = styled('div')`
  text-align: center;
  font-size: 14px;
`

// types
interface IIncomingProps {
  entity: IPlanObjective | IPlanAction | any
  isFullModal?: boolean
  isObjective?: boolean
  update: (data: any) => void
}

const CompleteAction: React.FC<IIncomingProps> = ({ entity, update, isFullModal, isObjective }) => {
  const { planTypeLevel } = React.useContext(ObjectiveContext) as any

  const runCompletionProcess = useStrategyCompletionProcess()

  if (!entity || !entity.completionAt) {
    return null
  }

  const { actionModifyAction, objectiveModifyObjective } = entity.currentPermissions

  // constants
  const dueDate = moment(entity.completionAt).format('D MMM YY')
  const isDueToday = moment(entity.completionAt).isSame(moment(), 'day')
  const isDueBefore = moment().isBefore(moment(entity.completionAt), 'day')
  const isActionCompleted = entity.completionPercentage === 100
  const shouldShowButton = (isObjective && objectiveModifyObjective) || (!isObjective && actionModifyAction)

  // methods
  const handleCompleteAction = async () => {
    try {
      let variables

      const actualCompletionDateId = entity.fields.find((field: IField) => field.name === 'actualCompletionDate')?.id

      if (isObjective) {
        const completionPercentageId = entity.fields.find((field: IField) => field.name === 'completionPercentage')?.id

        variables = {
          id: entity.id,
          input: {
            fieldValues: [
              { fieldId: completionPercentageId, value: isActionCompleted ? 0 : 100 },
              ...(!isActionCompleted ? [{ fieldId: actualCompletionDateId, value: moment().format('YYYY-MM-DD') }] : [])
            ]
          }
        }
      } else {
        variables = {
          id: entity.id,
          input: {
            completionPercentage: isActionCompleted ? 0 : 100,
            ...(!isActionCompleted && {
              fieldValues: [{ fieldId: actualCompletionDateId, value: moment().format('YYYY-MM-DD') }]
            })
          }
        }
      }

      await update({ variables })

      await runCompletionProcess({
        entityId: entity.id,
        object: isObjective ? SystemProcessObject.Objective : SystemProcessObject.Action,
        prev: entity.completionPercentage,
        next: isActionCompleted ? 0 : 100
      })
    } catch (error) {
      handleError(error, 'DetailsSection.handleCompleteAction')
    }
  }

  return (
    <Wrapper $isFullModal={isFullModal}>
      {isActionCompleted ? (
        <CheckCircle fontSize={'large'} htmlColor={colors.blue500} />
      ) : (
        <CheckCircleOutline fontSize={'large'} htmlColor={colors.gray200} />
      )}
      <InnerWrapper container size={{ md: 5 }}>
        <DueDateWrapper>{`${get(planTypeLevel, 'description') || OBJECT_ENTITY_NAMES.ACTION} due ${dueDate}`}</DueDateWrapper>
        <Grid container>
          <ProgressIndicator>{entity.completionPercentage === 100 ? 'COMPLETE' : 'IN PROGRESS'}</ProgressIndicator>
          {!isActionCompleted && !isDueBefore && (
            <>
              <TextSeparator>|</TextSeparator>
              <DueIndicator isDueToday={isDueToday}>{isDueToday ? 'DUE TODAY' : 'OVERDUE'}</DueIndicator>
            </>
          )}
        </Grid>
      </InnerWrapper>
      {shouldShowButton && (
        <Button $isFullModal={isFullModal} onClick={handleCompleteAction}>
          <ButtonLabel>
            {isActionCompleted ? `RE-OPEN ${isObjective ? '' : 'ACTION'}` : `COMPLETE ${isObjective ? '' : 'ACTION'}`}
          </ButtonLabel>
        </Button>
      )}
    </Wrapper>
  )
}

export default CompleteAction
